import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@cibli/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

interface EmailInputDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (email: string) => Promise<void>;
  isLoading?: boolean;
}

const emailSchema = z.object({
  email: z.string().email('formErrors.email').min(1, 'formErrors.required'),
});

type EmailFormData = z.infer<typeof emailSchema>;

export default function EmailInputDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: EmailInputDialogProps) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleFormSubmit = async (data: EmailFormData) => {
    await onSubmit(data.email);
    reset();
    onOpenChange(false);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl">
            {t('CVReception.emailDialog.title')}
          </DialogTitle>
          <DialogDescription className="text-base sm:text-lg">
            {t('CVReception.emailDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('CVReception.emailDialog.emailLabel')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('CVReception.emailDialog.emailPlaceholder')}
              {...register('email')}
              disabled={isLoading}
              aria-invalid={errors.email ? 'true' : 'false'}
            />
            {errors.email && (
              <p className="text-sm text-destructive">
                {t(errors.email.message || 'formErrors.email')}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isLoading}>
              {t('CVReception.emailDialog.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {t('CVReception.emailDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
